import { generateFingerprint } from './fingerprint';
import { SessionManager } from './session';
import { Transport } from './transport';
import {
  EventData,
  PageviewOptions,
  PulsoraConfig,
  PulsoraCore,
  PulsoraExtension,
  TrackingData,
} from './types';
import { debugLog, getReferrerSource, parseUrl } from './utils';

/**
 * Main Pulsora tracker implementation
 * Handles pageview tracking, custom events, and user identification
 */
export class Tracker implements PulsoraCore {
  private config?: PulsoraConfig;
  private transport?: Transport;
  private sessionManager: SessionManager;
  private visitorFingerprint?: string;
  private fingerprintPromise?: Promise<string>;
  private extensions = new Map<string, PulsoraExtension>();
  private initialized = false;

  constructor() {
    this.sessionManager = new SessionManager();
  }

  init(config: PulsoraConfig): void {
    if (this.initialized) {
      config.debug && debugLog('Already initialized');
      return;
    }

    if (typeof window === 'undefined') {
      throw new Error('Browser environment required');
    }

    this.config = {
      endpoint: 'https://api.pulsora.co/ingest',
      autoPageviews: true,
      debug: false,
      maxRetries: 10,
      retryBackoff: 1000,
      ...config,
    };

    this.transport = new Transport({
      endpoint: this.config.endpoint!,
      apiToken: this.config.apiToken,
      maxRetries: this.config.maxRetries!,
      retryBackoff: this.config.retryBackoff!,
      debug: this.config.debug!,
    });

    // Start fingerprint generation
    this.fingerprintPromise = generateFingerprint();
    this.fingerprintPromise.then((fingerprint) => {
      this.visitorFingerprint = fingerprint;
      this.config?.debug && debugLog('Fingerprint ready', fingerprint);
    });

    this.initialized = true;

    // Auto pageviews
    if (this.config.autoPageviews) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.pageview());
      } else {
        this.pageview();
      }
      this.setupSPATracking();
    }

    // Flush on unload
    this.setupUnloadHandlers();

    this.config.debug && debugLog('Initialized', this.config);
  }

  async pageview(options?: PageviewOptions): Promise<void> {
    if (!this.isReady()) return;

    const url = options?.url || location.href;
    const referrer = options?.referrer || document.referrer;
    const title = options?.title || document.title;

    const parsed = parseUrl(url);
    const referrerSource = getReferrerSource(referrer);

    const data: TrackingData = {
      type: 'pageview',
      visitor_fingerprint: await this.getVisitorFingerprint(),
      session_id: this.sessionManager.getSessionId(),
      url,
      path: parsed.path,
      referrer: referrer || undefined,
      referrer_source: referrerSource,
      title,
      utm_source: parsed.search.utm_source,
      utm_medium: parsed.search.utm_medium,
      utm_campaign: parsed.search.utm_campaign,
      utm_term: parsed.search.utm_term,
      utm_content: parsed.search.utm_content,
      timestamp: Date.now(),
    };

    this.transport!.send(data);
  }

  async event(eventName: string, eventData?: EventData): Promise<void> {
    if (!this.isReady() || !eventName) return;

    const url = location.href;
    const parsed = parseUrl(url);

    const data: TrackingData = {
      type: 'event',
      visitor_fingerprint: await this.getVisitorFingerprint(),
      session_id: this.sessionManager.getSessionId(),
      event_name: eventName,
      event_data: eventData,
      url,
      path: parsed.path,
      timestamp: Date.now(),
    };

    this.transport!.send(data);
  }

  async identify(customerId: string): Promise<void> {
    if (!this.isReady() || !customerId) return;

    this.sessionManager.setCustomerId(customerId);

    const data: TrackingData = {
      type: 'identify',
      visitor_fingerprint: await this.getVisitorFingerprint(),
      session_id: this.sessionManager.getSessionId(),
      customer_id: customerId,
      timestamp: Date.now(),
    };

    this.transport!.send(data);
    this.config?.debug && debugLog('User identified', customerId);
  }

  reset(): void {
    this.sessionManager.reset();
    this.config?.debug && debugLog('Session reset');
  }

  async getVisitorFingerprint(): Promise<string> {
    if (this.visitorFingerprint) return this.visitorFingerprint;
    if (this.fingerprintPromise) {
      this.visitorFingerprint = await this.fingerprintPromise;
      return this.visitorFingerprint;
    }
    this.fingerprintPromise = generateFingerprint();
    this.visitorFingerprint = await this.fingerprintPromise;
    return this.visitorFingerprint;
  }

  getSessionId(): string {
    return this.sessionManager.getSessionId();
  }

  isIdentified(): boolean {
    return this.sessionManager.isIdentified();
  }

  use(extension: PulsoraExtension): void {
    if (this.extensions.has(extension.name)) {
      this.config?.debug &&
        debugLog(`Extension ${extension.name} already loaded`);
      return;
    }
    this.extensions.set(extension.name, extension);
    extension.init(this);
    this.config?.debug && debugLog(`Extension ${extension.name} loaded`);
  }

  private isReady(): boolean {
    if (!this.initialized) {
      console.warn('[Pulsora] Not initialized');
      return false;
    }
    return true;
  }

  private setupSPATracking(): void {
    // Intercept pushState/replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      setTimeout(() => this.pageview(), 0);
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      setTimeout(() => this.pageview(), 0);
    };

    // Back/forward navigation
    addEventListener('popstate', () => {
      setTimeout(() => this.pageview(), 0);
    });
  }

  private setupUnloadHandlers(): void {
    const flush = () => this.transport?.flush();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });

    addEventListener('pagehide', flush);
    addEventListener('beforeunload', flush);
  }
}
